import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountAttributeTile,
  AccountAttributeTileType,
  AccountCopyAttributeTile,
  AccountCopyAttributeTileType,
  AccountProductEnum,
  AccountToggleAttributeTile,
  AccountToggleAttributeTileType,
  AccountTypeEnum,
  CreditScoreSuccess,
  ExternalPlaidAccount,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalSavingsAccount,
  ITile,
  ListTile,
  ListTileType,
  QuickActionTile,
  QuickActionTileType,
  TileStatus,
  TileType,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import AccountPageTiles from 'features/accounts/AccountPageTiles/AccountPageTiles';
import ExternalAccountPageTiles from 'features/accounts/ExternalAccountPageTiles/ExternalAccountPageTiles';
import ExternalTransactions, {
  ExternalAccountChildRef,
} from 'features/accounts/transactions/ExternalTransactions/ExternalTransactions';
import RecentTransactions, {
  ChildRef,
} from 'features/accounts/transactions/RecentTransactions/RecentTransactions';
import SweepstakesAccountCard from 'features/incentive/sweepstakes/components/SweepstakesAccountCard/SweepstakesAccountCard';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import {
  CreditScoreCard,
  CreditScoreTileStep,
} from 'shared/components/vxl/cards/CreditScoreCard/CreditScoreCard';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

import { getBannerData, getSourceName } from './AccountDetails.helpers';
import NotificationBanner from './NotificationBanner/NotificationBanner';

interface DefaultAccountSummaryInterface {
  account:
    | InternalAdvanceAccount
    | InternalCheckingAccount
    | InternalSavingsAccount
    | ExternalPlaidAccount
    | InternalChargeCardAccount;
  tiles: ITile[] | undefined;
  bottomSheetRef: RefObject<BottomSheetModal | null> | null;
}

export type AccountSummaryInterface = DefaultAccountSummaryInterface & {
  dataRef: React.Ref<ChildRef>;
  externalDataRef: React.Ref<ExternalAccountChildRef>;
  staleAccountSummary: boolean;
  selectedTab: string;
  refreshAccountSummary: () => void;
};

const Balance = ({
  title,
  balanceAmount,
}: {
  title: string;
  balanceAmount: string;
}) => {
  return (
    <View style={styles.balanceContainer}>
      <BodySmBoldText numberOfLines={1} data-testId="balanceTitle">
        {title}
      </BodySmBoldText>
      <ExpressiveSmText numberOfLines={1} testID="balance">
        {balanceAmount}
      </ExpressiveSmText>
    </View>
  );
};

const DefaultAccountSummary = ({
  account,
  tiles,
  dataRef,
  externalDataRef,
  staleAccountSummary,
  selectedTab,
  bottomSheetRef,
  refreshAccountSummary,
}: AccountSummaryInterface) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('accounts');
  const bannerData = getBannerData(tiles);
  const bannerMessage = account?.bannerMessagesR2[0];
  const creditScoreData = (account as InternalChargeCardAccount)
    ?.creditScore as CreditScoreSuccess;
  const fillToStep =
    creditScoreData?.ranges.findIndex(
      (item) => item?.description === creditScoreData?.grade,
    ) !== -1
      ? creditScoreData?.ranges.findIndex(
          (item) => item?.description === creditScoreData?.grade,
        ) + 1
      : 0;
  const checkingAndChargeCardAccountDetails = account as
    InternalCheckingAccount | InternalChargeCardAccount;
  const card = checkingAndChargeCardAccountDetails?.cards?.[0];
  const accountId = account?.id;
  const accountProduct = account?.product;

  const transferPressEvent = usePressableAnalytics({
    analyticsId: 'transfer',
    customAttributes: {
      category: accountProduct?.toLowerCase(),
      account_type: accountProduct?.toLowerCase(),
    },
  });
  const addMoneyPressEvent = usePressableAnalytics({
    analyticsId: 'addMoney',
    customAttributes: {
      account_type: accountProduct,
    },
  });

  const logMoveYourPayOnPressEvent = usePressableAnalytics({
    analyticsId: 'moveYourPay',
  });

  const moveMoneyPressEvent = usePressableAnalytics({
    analyticsId: 'moveMoney',
    customAttributes: {
      source_name: getSourceName(selectedTab) as string,
      category: accountProduct?.toLowerCase(),
      account_type: accountProduct?.toLowerCase(),
    },
  });

  const autoSavingStatus = (
    (tiles as ListTile[])?.find(
      (item) => item?.listTileType === ListTileType?.AccountAttributes,
    )?.tiles as AccountToggleAttributeTile[]
  )?.find(
    (tileItem) =>
      tileItem?.accountToggleAttributeTileType ===
      AccountToggleAttributeTileType?.AutoSaving,
  )?.isOn;

  const autoSavingPressEvent = usePressableAnalytics({
    analyticsId: 'autoSaving',
    customAttributes: {
      autosaving_status: autoSavingStatus ? 'on' : 'off',
      account_type: accountProduct?.toLowerCase(),
    },
  });

  const logAdvanceMakePaymentClickEvent = usePressableAnalytics({
    analyticsId: 'makePaymentClicked',
    customAttributes: {
      account_type: 'advance',
      cta: 'Make a payment',
    },
  });

  const viewCardPressEvent = usePressableAnalytics({
    analyticsId: 'viewCard',
    customAttributes: {
      source_name: getSourceName(selectedTab) as string,
      category:
        accountProduct === AccountProductEnum.Checking
          ? accountProduct?.toLowerCase()
          : account?.name?.toLowerCase(),
      account_type: accountProduct?.toLowerCase(),
      card_type: card?.type?.toLowerCase() as string,
    },
  });

  const chargeCardViewCardPressEvent = usePressableAnalytics({
    analyticsId: 'chargeCardViewCard',
    customAttributes: {
      card_type: card?.type?.toLowerCase() as string,
    },
  });

  const handleQuickActionPress = (tile: QuickActionTile) => {
    switch (tile.quickActionTileType) {
      case QuickActionTileType.Transfer:
        transferPressEvent();
        router.push({
          pathname: '/move-money/transfer/transfer-money',
          params: {
            ...(accountProduct !== AccountProductEnum.Checking && {
              typeOfAccount: accountProduct,
            }),
            route: `/accounts/${accountId}`,
          },
        });
        break;
      case QuickActionTileType.AddMoney:
        addMoneyPressEvent();
        router.push({
          pathname: '/move-money/transfer/transfer-money',
          params: {
            ...(accountProduct !== AccountProductEnum.Checking && {
              typeOfAccount: accountProduct,
            }),
            route: `/accounts/${accountId}`,
          },
        });
        break;
      case QuickActionTileType.ManageCard:
      case QuickActionTileType.ChargeCardViewCard:
        chargeCardViewCardPressEvent();
        navigateToCard();
        break;
      case QuickActionTileType.PayCard:
        router.push({
          pathname: '/believe/make-payment',
          params: { id: accountId },
        });
        break;
      case QuickActionTileType.CheckingViewCard:
        viewCardPressEvent();
        navigateToCard();
        break;
    }
  };

  const navigateToCard = () => {
    router.push({
      pathname: '/myvaro/card/[cardId]',
      params: { cardId: card?.id },
    });
  };

  const handleTilePress = (tile: ITile) => {
    if (tile.tileType === TileType.MoveMoney) {
      moveMoneyPressEvent();
    }

    if (
      tile.tileType === TileType.AddMoney ||
      tile.tileType === TileType.MoveMoney
    ) {
      router.push('/move-money/transfer/transfer-money');
      return;
    }
    if (tile.tileType === TileType.QuickAction) {
      if (accountProduct === AccountProductEnum.Advance) {
        logAdvanceMakePaymentClickEvent();
        router.push({
          pathname: '/accounts/varo-advance/manual-repayment',
          params: {
            id: accountId,
          },
        });
      }
      handleQuickActionPress(tile as QuickActionTile);
      return;
    }
    if (tile.tileType === TileType.AccountAttribute) {
      onPressAccountAttributeTile(
        tile as
          | AccountCopyAttributeTile
          | AccountToggleAttributeTile
          | AccountAttributeTile,
      );
      return;
    }
    if (tile.tileType === TileType.ContentCta) {
      router.push({
        pathname: '/accounts/savings-tracker',
        params: {
          id: accountId,
        },
      });
      return;
    }
  };

  const onPressAccountAttributeTile = (
    tile:
      | AccountCopyAttributeTile
      | AccountToggleAttributeTile
      | AccountAttributeTile,
  ) => {
    if (tile.__typename === Typenames.AccountAttributeTile) {
      if (accountProduct === AccountProductEnum.ChargeCard) {
        if (
          tile.accountAttributeTileType === AccountAttributeTileType.CardBalance
        ) {
          router.push({
            pathname: '/accounts/manage-account',
            params: { accountId: accountId },
          });
        }
      }
    }
    if (tile.__typename === Typenames.AccountCopyAttributeTile) {
      if (accountProduct === AccountProductEnum.Advance) {
        return;
      }
      if (
        tile.accountCopyAttributeTileType ===
        AccountCopyAttributeTileType.CurrentApy
      ) {
        return router.push({
          pathname: '/accounts/current-apy',
          params: { id: accountId },
        });
      }
      router.push({
        pathname: '/accounts/manage-account',
        params: { accountId: accountId },
      });
      return;
    }
    if (tile.__typename === Typenames.AccountToggleAttributeTile) {
      if (accountProduct === AccountProductEnum.ChargeCard) {
        if (
          tile.accountToggleAttributeTileType ===
          AccountToggleAttributeTileType.SafePay
        ) {
          router.push({
            pathname: `/accounts/[id]/safe-credit-builder`,
            params: { id: accountId },
          });
        } else if (
          tile.accountToggleAttributeTileType ===
          AccountToggleAttributeTileType.ChargeCardMoveYourPay
        ) {
          logMoveYourPayOnPressEvent();
          router.push({
            pathname: '/believe/move-your-pay',
          });
        }
      } else {
        autoSavingPressEvent();
        router.push({
          pathname: '/accounts/automatic-savings',
        });
        return;
      }
    }
  };

  const advanceTiles = [
    ...((account as InternalAdvanceAccount)?.activeAdvance?.detailsV2
      ?.flatMap((group) => group.items)
      ?.map((detailsOfTile, index, array) => {
        return {
          ...detailsOfTile,
          status: TileStatus.Loaded,
          __typename: Typenames.AccountCopyAttributeTile,
          tileType: TileType.AccountAttribute,
          showsChevron: false,
          showDivider: index === array.length - 1,
        };
      }) || []),
    ...((account as InternalAdvanceAccount)?.activeAdvance?.canRepay
      ? [
          {
            tileType: TileType.List,
            id: 'internal-advance-account-quick-actions',
            listTileType: ListTileType.QuickActions,
            tiles: [
              {
                id: 'internal-advance-account-pay-action',
                tileType: TileType.QuickAction,
                title: translatedText('manageAccount.makePayment'),
              },
            ],
          },
        ]
      : []),
  ];

  if (!account) {
    return null;
  }

  return (
    <View>
      {staleAccountSummary ? (
        <View style={styles.balanceContainer}>
          <LoadingSpinner />
        </View>
      ) : (
        <>
          {bannerMessage || bannerData ? (
            <NotificationBanner
              bannerMessage={bannerMessage}
              bannerData={bannerData}
              bottomSheetRef={bottomSheetRef}
              accountId={accountId}
              refreshAccountSummary={refreshAccountSummary}
            />
          ) : null}
          {account?.displayBalance ? (
            <AmplitudeMask>
              <Balance
                title={account.displayBalance.title}
                balanceAmount={account.displayBalance.amount?.formatted}
              />
            </AmplitudeMask>
          ) : null}

          {account?.type === AccountTypeEnum.External ? (
            <AmplitudeMask>
              <ExternalAccountPageTiles
                accountName={account.institution.name}
                lastUpdated={(account as ExternalPlaidAccount).lastUpdated}
                accountNumberLast4={account.accountNumberLast4 ?? ''}
                product={accountProduct}
              />
            </AmplitudeMask>
          ) : null}
          {tiles ? (
            <AccountPageTiles
              tiles={[...tiles, ...(advanceTiles as ITile[])]}
              handleTilePress={handleTilePress}
              productType={accountProduct}
            />
          ) : null}

          {accountProduct === AccountProductEnum.ChargeCard ? (
            <SweepstakesAccountCard />
          ) : null}

          {accountProduct === AccountProductEnum.ChargeCard &&
          creditScoreData.__typename === Typenames.CreditScoreSuccess ? (
            <>
              <View style={styles.divider} />
              <CreditScoreCard
                creditScore={creditScoreData?.score}
                creditScoreDelta={
                  creditScoreData?.scoreDelta ? creditScoreData?.scoreDelta : 0
                }
                fillToStep={fillToStep as CreditScoreTileStep}
                isDisabled={false}
                creditScoreDeltaTagline={
                  creditScoreData?.lastUpdated
                    ? creditScoreData?.lastUpdated
                    : creditScoreData?.description
                }
                handleCardPress={() => {
                  router.push('/credit/about-credit-score');
                }}
                ranges={creditScoreData?.ranges}
                grade={creditScoreData?.grade}
              />
            </>
          ) : null}
          <View style={styles.divider} />
          {account?.type === AccountTypeEnum.External ? (
            <ExternalTransactions
              accountId={accountId}
              first={50}
              ref={externalDataRef}
              accountType={accountProduct}
            />
          ) : (
            <RecentTransactions
              accountId={accountId}
              cachedTransactions={
                (account as InternalAdvanceAccount)?.activeAdvance
                  ?.advancePayments || []
              }
              fetchTransactions={accountProduct !== AccountProductEnum.Advance}
              ref={dataRef}
              accountType={accountProduct}
            />
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.xs,
    padding: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
});

export default DefaultAccountSummary;
